import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Bot, Database, UserCheck, Zap } from 'lucide-react';

export const SolutionSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      ref={sectionRef}
      id="solution"
      className="py-20 relative"
      data-testid="solution-section"
    >
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Your Revenue Team.
            <br />
            <span className="text-[#6C4DFF]">Not Just Software.</span>
          </h2>

          <p className="text-lg text-white/60 max-w-xl mx-auto mb-6">
            ScaleHQ combines AI, systems, and a real person dedicated to 
            working your pipeline and closing more business.
          </p>

          {/* Speed-to-lead proof stat */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-[#6C4DFF]/10 border border-[#6C4DFF]/20"
          >
            <Zap className="w-5 h-5 text-[#6C4DFF]" />
            <div className="text-left">
              <div className="text-lg font-bold text-white">21x more likely</div>
              <div className="text-xs text-white/50">to qualify a lead when contacted within 5 minutes</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Three pillars flow */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col md:flex-row items-center justify-center gap-4"
        >
          <div className="flex items-center gap-4 px-5 py-4 bg-white/5 rounded-2xl border border-white/8 w-full md:w-auto">
            <div className="w-11 h-11 rounded-xl bg-[#6C4DFF]/15 flex items-center justify-center flex-shrink-0">
              <Bot className="w-5 h-5 text-[#6C4DFF]" />
            </div>
            <div className="text-left">
              <p className="text-white font-medium">AI Captures</p>
              <p className="text-sm text-white/50">Every lead, every call</p>
            </div>
          </div>

          <div className="hidden md:block text-white/20 text-xl">→</div>
          <div className="md:hidden text-white/20 text-xl">↓</div>

          <div className="flex items-center gap-4 px-5 py-4 bg-white/5 rounded-2xl border border-white/8 w-full md:w-auto">
            <div className="w-11 h-11 rounded-xl bg-[#6C4DFF]/15 flex items-center justify-center flex-shrink-0">
              <Database className="w-5 h-5 text-[#6C4DFF]" />
            </div>
            <div className="text-left">
              <p className="text-white font-medium">CRM Organizes</p>
              <p className="text-sm text-white/50">Full pipeline visibility</p>
            </div>
          </div>

          <div className="hidden md:block text-white/20 text-xl">→</div>
          <div className="md:hidden text-white/20 text-xl">↓</div>

          <div className="flex items-center gap-4 px-5 py-4 bg-[#6C4DFF]/10 rounded-2xl border border-[#6C4DFF]/30 w-full md:w-auto">
            <div className="w-11 h-11 rounded-xl bg-[#6C4DFF]/20 flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-5 h-5 text-[#6C4DFF]" />
            </div>
            <div className="text-left">
              <p className="text-white font-medium">Your Closer Works It</p>
              <p className="text-sm text-white/50">Follows up & books jobs</p>
            </div>
          </div>
        </motion.div>

        {/* Differentiator line */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center text-sm text-white/40 mt-8"
        >
          Not just automation. A revenue support system with technology and human follow-up.
        </motion.p>
      </div>
    </section>
  );
};
